/**
 * Les corrections d'heures d'une intervention VALIDÉE (lot U2b, ADR-0016) : la liste de ce qui a été corrigé, et — pour qui
 * valide — le formulaire pour corriger. L'équipe d'une intervention validée est figée : une heure oubliée ou fausse se corrige
 * par une ligne de plus, jamais en effaçant l'ancienne valeur, qui reste lisible dans la liste. Une raison est obligatoire.
 *
 * Le refus d'un geste a son propre état (jamais effacé par la relecture qui suit) ; un conflit relit l'écran PUIS le dit.
 */
import { useEffect, useState } from 'react';

import { ApiError, api, type AjustementHeures, type BriefingIntervention, type Technicien } from '../api/client';
import { jourFr, jourValide } from '../domaine/planning';
import { lireHeuresSaisies, lireRaisonCorrection, phraseCorrection, phraseCorrectionEnregistree } from '../domaine/planning-interventions';

export function CorrectionsHeuresIntervention({
  interventionId,
  jours,
  peutCorriger,
  rechargement,
  onChange,
  onMessage,
}: {
  interventionId: string;
  jours: BriefingIntervention['jours'];
  /** Le droit de valider : lui seul corrige les heures d'une intervention validée. */
  peutCorriger: boolean;
  rechargement: number;
  onChange: () => void;
  onMessage: (message: string) => void;
}) {
  const [liste, setListe] = useState<readonly AjustementHeures[] | null>(null);
  const [erreurLecture, setErreurLecture] = useState<string | null>(null);
  const [ouvert, setOuvert] = useState(false);
  const [techniciens, setTechniciens] = useState<readonly Technicien[]>([]);
  const [jour, setJour] = useState(() => jours[0]?.day ?? '');
  const [technicianId, setTechnicianId] = useState('');
  const [duree, setDuree] = useState('');
  const [raison, setRaison] = useState('');
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    let annule = false;
    api
      .ajustementsHeures(interventionId)
      .then((r) => {
        if (annule) return;
        setListe(r.items);
        setErreurLecture(null);
      })
      .catch((e: unknown) => {
        if (!annule) setErreurLecture(e instanceof ApiError ? e.message : 'Les corrections n’ont pas pu être lues.');
      });
    return () => {
      annule = true;
    };
  }, [interventionId, rechargement]);

  useEffect(() => {
    if (!ouvert) return;
    let annule = false;
    api
      .techniciensPlanning(false)
      .then((r) => {
        if (!annule) setTechniciens(r.items.filter((t) => !t.archived));
      })
      .catch(() => {
        if (!annule) setErreur('La liste des techniciens n’a pas pu être lue.');
      });
    return () => {
      annule = true;
    };
  }, [ouvert]);

  const corriger = async () => {
    setErreur(null);
    const minutes = lireHeuresSaisies(duree);
    if (minutes === 'ERREUR') {
      setErreur('Écrivez une durée comme 3, 7,5, 1h30 ou 45 min (de 1 minute à 24 heures).');
      return;
    }
    if (minutes === null) {
      setErreur('Donnez les heures retenues (une durée comme 1h30).');
      return;
    }
    if (!jourValide(jour)) {
      setErreur('Choisissez le jour de l’intervention.');
      return;
    }
    const technicien = techniciens.find((t) => t.id === technicianId);
    if (!technicien) {
      setErreur('Choisissez le technicien.');
      return;
    }
    const motif = lireRaisonCorrection(raison);
    if (motif === null) {
      setErreur('La raison de la correction est obligatoire (trois caractères au moins).');
      return;
    }
    setEnCours(true);
    try {
      await api.ajusterHeures(interventionId, { jour, technicianId: technicien.id, minutes, reason: motif });
      setDuree('');
      setRaison('');
      onMessage(phraseCorrectionEnregistree(technicien.name, jour, minutes));
      onChange();
    } catch (e) {
      // 409 : l'intervention ou ses heures ont changé depuis l'affichage. On relit, PUIS on le dit.
      if (e instanceof ApiError && e.status === 409) onChange();
      setErreur(e instanceof ApiError || e instanceof Error ? e.message : 'Correction impossible.');
    } finally {
      setEnCours(false);
    }
  };

  return (
    <div className="corrections-heures">
      <h3>Corrections d’heures</h3>
      {erreurLecture && <div className="message erreur">{erreurLecture}</div>}
      {liste !== null && liste.length === 0 && <p className="aide">Aucune correction : les heures retenues sont celles de la validation.</p>}
      {liste !== null && liste.length > 0 && (
        <ul className="briefing-jours">
          {liste.map((a) => (
            <li key={a.id}>
              {phraseCorrection(a)}
              <span className="aide"> · raison : {a.reason}</span>
            </li>
          ))}
        </ul>
      )}

      {peutCorriger &&
        (ouvert ? (
          <div className="carte saisie-heures">
            <p className="aide">
              L’intervention est validée : ses heures ne se modifient plus, on ajoute une correction (avec sa raison). Le technicien peut ne
              pas avoir fait partie de l’équipe ce jour-là. La facturation suit le palier de quinze minutes supérieur.
            </p>
            <div className="saisie-ligne">
              <label htmlFor="correction-jour">Jour</label>
              <select id="correction-jour" value={jour} onChange={(e) => setJour(e.target.value)}>
                {jours.map((j) => (
                  <option key={j.day} value={j.day}>
                    {jourFr(j.day)}
                  </option>
                ))}
              </select>
              <label htmlFor="correction-technicien">Technicien</label>
              <select id="correction-technicien" value={technicianId} onChange={(e) => setTechnicianId(e.target.value)}>
                <option value="">Choisir…</option>
                {techniciens.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
              <label htmlFor="correction-duree">Heures retenues</label>
              <input id="correction-duree" type="text" inputMode="decimal" placeholder="3, 7,5, 1h30, 45 min" value={duree} onChange={(e) => setDuree(e.target.value)} />
            </div>
            <div className="saisie-ligne">
              <label htmlFor="correction-raison">Raison</label>
              <input id="correction-raison" type="text" maxLength={500} placeholder="Pourquoi cette correction (obligatoire)" value={raison} onChange={(e) => setRaison(e.target.value)} />
              <button type="button" disabled={enCours} onClick={() => void corriger()}>
                Enregistrer la correction
              </button>
              <button type="button" className="secondaire" disabled={enCours} onClick={() => setOuvert(false)}>
                Fermer
              </button>
            </div>
            {erreur && <div className="message erreur">{erreur}</div>}
          </div>
        ) : (
          <div className="saisie-ligne">
            <button type="button" className="secondaire" onClick={() => setOuvert(true)}>
              Corriger des heures
            </button>
          </div>
        ))}
    </div>
  );
}
